import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Crown, Users, Save, Loader2, Plus, Settings, Shield,
  Calendar, ShoppingBag, Heart, BookOpen, Trophy, MessageSquare,
  Camera, FolderOpen, UserPlus, UserMinus, ChevronRight,
  Megaphone, Building2, ArrowLeft, Check, X, Search, Palette,
  Dog, Cat, Bird, Fish, Rabbit, PawPrint, MapPin, Globe
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Feature icons mapping
const FEATURE_ICONS = {
  events: Calendar,
  marketplace: ShoppingBag,
  breeding_tools: Heart,
  health_records: Heart,
  competitions: Trophy,
  training_logs: BookOpen,
  community_forums: MessageSquare,
  photo_albums: Camera,
  member_directory: Users,
  resources_library: FolderOpen
};

// Pet type options
const PET_TYPES = [
  { id: 'dog', label: 'Dogs', icon: Dog },
  { id: 'cat', label: 'Cats', icon: Cat },
  { id: 'bird', label: 'Birds', icon: Bird },
  { id: 'fish', label: 'Fish', icon: Fish },
  { id: 'rabbit', label: 'Rabbits', icon: Rabbit },
  { id: 'reptile', label: 'Reptiles', icon: PawPrint },
  { id: 'rodent', label: 'Rodents', icon: PawPrint },
  { id: 'horse', label: 'Horses', icon: PawPrint },
  { id: 'exotic', label: 'Exotic', icon: PawPrint },
  { id: 'multi', label: 'Multiple', icon: Heart },
];

// Color presets
const COLOR_PRESETS = [
  { name: 'Classic Brown', primary: '#c9762b', secondary: '#a65d1f', accent: '#e3c992' },
  { name: 'Forest Green', primary: '#166534', secondary: '#14532d', accent: '#86efac' },
  { name: 'Ocean Blue', primary: '#1e40af', secondary: '#1e3a8a', accent: '#93c5fd' },
  { name: 'Royal Purple', primary: '#7c3aed', secondary: '#6d28d9', accent: '#c4b5fd' },
  { name: 'Sunset Orange', primary: '#ea580c', secondary: '#c2410c', accent: '#fed7aa' },
  { name: 'Rose Pink', primary: '#db2777', secondary: '#be185d', accent: '#fbcfe8' },
  { name: 'Slate Gray', primary: '#475569', secondary: '#334155', accent: '#cbd5e1' },
  { name: 'Teal', primary: '#0d9488', secondary: '#0f766e', accent: '#99f6e4' },
];

// US States list
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// Countries list
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'OTHER', name: 'Other' },
];

// Languages list
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'nl', name: 'Dutch' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'other', name: 'Other' },
];

export default function ClubCreatorDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [myClubs, setMyClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState(null);
  const [clubDashboard, setClubDashboard] = useState(null);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [savingAdPolicy, setSavingAdPolicy] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [promotingUser, setPromotingUser] = useState(null);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [pendingFeatures, setPendingFeatures] = useState({});
  const [pendingAdPolicy, setPendingAdPolicy] = useState({});
  
  // Club settings state
  const [clubSettings, setClubSettings] = useState({
    name: '',
    tagline: '',
    motto: '',
    description: '',
    mission: '',
    purpose: '',
    pet_types: [],
    country: 'US',
    state: '',
    region: '',
    default_language: 'en',
    primary_color: '#c9762b',
    secondary_color: '#a65d1f',
    accent_color: '#e3c992',
    banner_color: '#fffbeb',
    nav_color: '#78350f',
    email: '',
    website_url: '',
    allows_free_members: true,
  });

  const isPremium = user?.membership_tier === 'premium';

  // Fetch my created clubs
  const fetchMyClubs = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/api/clubs/creator/my-clubs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyClubs(response.data.clubs || []);
      
      // Auto-select first club if none selected
      if (!selectedClubId && response.data.clubs?.length > 0) {
        setSelectedClubId(response.data.clubs[0].club_id);
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
      if (error.response?.status === 403) {
        toast.error('Premium membership required to access creator features');
      }
    } finally {
      setLoading(false);
    }
  }, [token, selectedClubId]);

  // Fetch feature catalog
  const fetchFeatureCatalog = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/clubs/features/catalog`);
      setFeatureCatalog(response.data.features || []);
    } catch (error) {
      console.error('Error fetching feature catalog:', error);
    }
  }, []);

  // Fetch club dashboard
  const fetchClubDashboard = useCallback(async (clubId) => {
    if (!token || !clubId) return;
    setLoadingDashboard(true);
    try {
      const response = await axios.get(`${API}/api/clubs/creator/${clubId}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClubDashboard(response.data);
      setPendingFeatures(response.data.features || {});
      setPendingAdPolicy(response.data.ad_policy || {});
      
      // Populate club settings from dashboard data
      const club = response.data.club;
      if (club) {
        setClubSettings({
          name: club.name || '',
          tagline: club.tagline || '',
          motto: club.motto || '',
          description: club.description || '',
          mission: club.mission || '',
          purpose: club.purpose || '',
          pet_types: club.pet_types || [],
          country: club.country || 'US',
          state: club.state || '',
          region: club.region || '',
          default_language: club.default_language || 'en',
          primary_color: club.primary_color || '#c9762b',
          secondary_color: club.secondary_color || '#a65d1f',
          accent_color: club.accent_color || '#e3c992',
          banner_color: club.banner_color || '#fffbeb',
          nav_color: club.nav_color || '#78350f',
          email: club.email || '',
          website_url: club.website_url || '',
          allows_free_members: club.allows_free_members !== false,
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load club dashboard');
    } finally {
      setLoadingDashboard(false);
    }
  }, [token]);

  // Fetch club members
  const fetchMembers = useCallback(async (clubId) => {
    if (!token || !clubId) return;
    try {
      const response = await axios.get(`${API}/api/clubs/creator/${clubId}/members?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchMyClubs();
    fetchFeatureCatalog();
  }, [fetchMyClubs, fetchFeatureCatalog]);

  useEffect(() => {
    if (selectedClubId) {
      fetchClubDashboard(selectedClubId);
      fetchMembers(selectedClubId);
    }
  }, [selectedClubId, fetchClubDashboard, fetchMembers]);

  // Save features
  const saveFeatures = async () => {
    if (!selectedClubId) return;
    setSavingFeatures(true);
    try {
      await axios.put(
        `${API}/api/clubs/creator/${selectedClubId}/features`,
        pendingFeatures,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Features updated successfully');
      fetchClubDashboard(selectedClubId);
    } catch (error) {
      console.error('Error saving features:', error);
      toast.error('Failed to save features');
    } finally {
      setSavingFeatures(false);
    }
  };

  // Save ad policy
  const saveAdPolicy = async () => {
    if (!selectedClubId) return;
    setSavingAdPolicy(true);
    try {
      await axios.put(
        `${API}/api/clubs/creator/${selectedClubId}/ad-policy`,
        pendingAdPolicy,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ad policy updated successfully');
      fetchClubDashboard(selectedClubId);
    } catch (error) {
      console.error('Error saving ad policy:', error);
      toast.error('Failed to save ad policy');
    } finally {
      setSavingAdPolicy(false);
    }
  };

  // Save club settings (branding, pet types, etc.)
  const saveClubSettings = async () => {
    if (!selectedClubId) return;
    setSavingSettings(true);
    try {
      await axios.put(
        `${API}/api/clubs/${selectedClubId}`,
        {
          name: clubSettings.name,
          tagline: clubSettings.tagline,
          motto: clubSettings.motto || null,
          description: clubSettings.description,
          mission: clubSettings.mission,
          purpose: clubSettings.purpose || null,
          pet_types: clubSettings.pet_types,
          country: clubSettings.country,
          state: clubSettings.state || null,
          region: clubSettings.region || null,
          default_language: clubSettings.default_language,
          primary_color: clubSettings.primary_color,
          secondary_color: clubSettings.secondary_color,
          accent_color: clubSettings.accent_color,
          banner_color: clubSettings.banner_color,
          nav_color: clubSettings.nav_color,
          email: clubSettings.email || null,
          website_url: clubSettings.website_url || null,
          allows_free_members: clubSettings.allows_free_members,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Club settings updated successfully');
      fetchClubDashboard(selectedClubId);
      fetchMyClubs(); // Refresh the sidebar
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Toggle pet type
  const togglePetType = (petId) => {
    setClubSettings(prev => ({
      ...prev,
      pet_types: prev.pet_types.includes(petId)
        ? prev.pet_types.filter(p => p !== petId)
        : [...prev.pet_types, petId]
    }));
  };

  // Select color preset
  const selectColorPreset = (preset) => {
    setClubSettings(prev => ({
      ...prev,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent,
    }));
  };

  // Promote user to admin
  const promoteToAdmin = async (userId, username) => {
    if (!selectedClubId) return;
    setPromotingUser(userId);
    try {
      await axios.post(
        `${API}/api/clubs/creator/${selectedClubId}/admins/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${username} is now an admin`);
      fetchMembers(selectedClubId);
    } catch (error) {
      console.error('Error promoting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to promote user');
    } finally {
      setPromotingUser(null);
    }
  };

  // Demote admin to member
  const demoteAdmin = async (userId, username) => {
    if (!selectedClubId) return;
    setPromotingUser(userId);
    try {
      await axios.delete(
        `${API}/api/clubs/creator/${selectedClubId}/admins/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${username} is now a regular member`);
      fetchMembers(selectedClubId);
    } catch (error) {
      console.error('Error demoting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to demote user');
    } finally {
      setPromotingUser(null);
    }
  };

  // Filter members by search
  const filteredMembers = members.filter(m => 
    !memberSearch || 
    m.username?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.display_name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.email?.toLowerCase().includes(memberSearch.toLowerCase())
  );

  // Not premium - show upgrade prompt
  if (!isPremium && !loading) {
    return (
      <div className="min-h-screen bg-muted/30 py-12" data-testid="creator-dashboard-upgrade">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <Crown className="w-16 h-16 mx-auto text-amber-500 mb-4" />
          <h1 className="text-3xl font-heading font-medium mb-4">Premium Feature</h1>
          <p className="text-muted-foreground mb-8">
            Club creation is an exclusive Premium feature. Upgrade to Premium to create 
            and manage your own clubs, select features, and assign admins.
          </p>
          <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600">
            <Link to="/membership/upgrade">
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Premium
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="creator-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white py-8">
        <div className="container mx-auto px-4">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <Crown className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-heading font-medium">Club Creator Dashboard</h1>
              <p className="text-white/80 text-sm">Manage your clubs, features, and team</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar - Club List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  My Clubs
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {myClubs.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-sm text-muted-foreground mb-4">
                      You haven't created any clubs yet.
                    </p>
                    <Button size="sm" asChild>
                      <Link to="/clubs/create">
                        <Plus className="w-4 h-4 mr-1" />
                        Create Club
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <>
                    {myClubs.map(club => (
                      <button
                        key={club.club_id}
                        onClick={() => setSelectedClubId(club.club_id)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedClubId === club.club_id
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background hover:bg-muted border-transparent'
                        }`}
                        data-testid={`club-select-${club.club_slug}`}
                      >
                        <div className="font-medium text-sm">{club.club_name}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {club.member_count} members
                          </Badge>
                          <Badge 
                            variant={club.status === 'active' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {club.status}
                          </Badge>
                        </div>
                      </button>
                    ))}
                    <Button size="sm" variant="outline" className="w-full mt-4" asChild>
                      <Link to="/clubs/create">
                        <Plus className="w-4 h-4 mr-1" />
                        Create New Club
                      </Link>
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {!selectedClubId ? (
              <Card className="py-12 text-center">
                <CardContent>
                  <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Select a club to manage</p>
                </CardContent>
              </Card>
            ) : loadingDashboard ? (
              <Card className="py-12 text-center">
                <CardContent>
                  <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
                </CardContent>
              </Card>
            ) : clubDashboard ? (
              <div className="space-y-6">
                {/* Club Overview Card */}
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{clubDashboard.club.name}</CardTitle>
                        <CardDescription>{clubDashboard.club.tagline}</CardDescription>
                      </div>
                      <Badge variant={clubDashboard.club.status === 'active' ? 'default' : 'secondary'}>
                        {clubDashboard.club.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <div className="text-2xl font-bold">{clubDashboard.stats.member_count}</div>
                        <div className="text-sm text-muted-foreground">Members</div>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <div className="text-2xl font-bold">{clubDashboard.stats.admin_count}</div>
                        <div className="text-sm text-muted-foreground">Admins</div>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <div className="text-2xl font-bold">{clubDashboard.stats.officer_count}</div>
                        <div className="text-sm text-muted-foreground">Officers</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid grid-cols-4 w-full">
                    <TabsTrigger value="settings" data-testid="tab-settings">
                      <Palette className="w-4 h-4 mr-2" />
                      Settings
                    </TabsTrigger>
                    <TabsTrigger value="overview" data-testid="tab-overview">
                      <Settings className="w-4 h-4 mr-2" />
                      Features
                    </TabsTrigger>
                    <TabsTrigger value="members" data-testid="tab-members">
                      <Users className="w-4 h-4 mr-2" />
                      Members
                    </TabsTrigger>
                    <TabsTrigger value="ads" data-testid="tab-ads">
                      <Megaphone className="w-4 h-4 mr-2" />
                      Ads
                    </TabsTrigger>
                  </TabsList>

                  {/* Settings Tab - Pet Types, Branding, Basic Info */}
                  <TabsContent value="settings" className="space-y-4">
                    {/* Basic Info */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Basic Information</CardTitle>
                        <CardDescription>Update your club's name and description</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="club-name">Club Name</Label>
                            <Input
                              id="club-name"
                              value={clubSettings.name}
                              onChange={(e) => setClubSettings(prev => ({ ...prev, name: e.target.value }))}
                              data-testid="settings-name-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="club-tagline">Tagline</Label>
                            <Input
                              id="club-tagline"
                              value={clubSettings.tagline}
                              onChange={(e) => setClubSettings(prev => ({ ...prev, tagline: e.target.value }))}
                              data-testid="settings-tagline-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="club-motto">Club Motto</Label>
                          <Input
                            id="club-motto"
                            value={clubSettings.motto}
                            onChange={(e) => setClubSettings(prev => ({ ...prev, motto: e.target.value }))}
                            placeholder="e.g., 'Where Every Pup is a Champion'"
                            data-testid="settings-motto-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="club-description">Description</Label>
                          <Textarea
                            id="club-description"
                            value={clubSettings.description}
                            onChange={(e) => setClubSettings(prev => ({ ...prev, description: e.target.value }))}
                            rows={2}
                            data-testid="settings-description-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="club-purpose">Purpose Statement</Label>
                          <Textarea
                            id="club-purpose"
                            value={clubSettings.purpose}
                            onChange={(e) => setClubSettings(prev => ({ ...prev, purpose: e.target.value }))}
                            rows={2}
                            placeholder="Why does this club exist? What need does it meet?"
                            data-testid="settings-purpose-input"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="club-email">Contact Email</Label>
                            <Input
                              id="club-email"
                              type="email"
                              value={clubSettings.email}
                              onChange={(e) => setClubSettings(prev => ({ ...prev, email: e.target.value }))}
                            />
                          </div>
                          <div>
                            <Label htmlFor="club-website">Website</Label>
                            <Input
                              id="club-website"
                              type="url"
                              value={clubSettings.website_url}
                              onChange={(e) => setClubSettings(prev => ({ ...prev, website_url: e.target.value }))}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Location */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Location</CardTitle>
                        <CardDescription>Where is your club based?</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="club-country">Country</Label>
                            <select
                              id="club-country"
                              value={clubSettings.country}
                              onChange={(e) => setClubSettings(prev => ({ ...prev, country: e.target.value, state: '' }))}
                              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                              data-testid="settings-country-select"
                            >
                              {COUNTRIES.map(c => (
                                <option key={c.code} value={c.code}>{c.name}</option>
                              ))}
                            </select>
                          </div>
                          {clubSettings.country === 'US' && (
                            <div>
                              <Label htmlFor="club-state">State</Label>
                              <select
                                id="club-state"
                                value={clubSettings.state}
                                onChange={(e) => setClubSettings(prev => ({ ...prev, state: e.target.value }))}
                                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                data-testid="settings-state-select"
                              >
                                <option value="">Select state...</option>
                                {US_STATES.map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div>
                            <Label htmlFor="club-region">Region/Area</Label>
                            <Input
                              id="club-region"
                              value={clubSettings.region}
                              onChange={(e) => setClubSettings(prev => ({ ...prev, region: e.target.value }))}
                              placeholder="e.g., Pacific Northwest"
                              data-testid="settings-region-input"
                            />
                          </div>
                        </div>
                        <div className="mt-4">
                          <Label htmlFor="club-language">Preferred Language</Label>
                          <select
                            id="club-language"
                            value={clubSettings.default_language}
                            onChange={(e) => setClubSettings(prev => ({ ...prev, default_language: e.target.value }))}
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                            data-testid="settings-language-select"
                          >
                            {LANGUAGES.map(l => (
                              <option key={l.code} value={l.code}>{l.name}</option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground mt-1">Primary language for club communications</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pet Types */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Pet Types</CardTitle>
                        <CardDescription>What pets does your club focus on?</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-5 gap-2">
                          {PET_TYPES.map(pet => {
                            const Icon = pet.icon;
                            const isSelected = clubSettings.pet_types?.includes(pet.id);
                            return (
                              <button
                                key={pet.id}
                                type="button"
                                onClick={() => togglePetType(pet.id)}
                                className={`flex flex-col items-center gap-1 p-3 rounded-lg border text-center transition-all ${
                                  isSelected 
                                    ? 'bg-primary/10 border-primary' 
                                    : 'bg-background hover:bg-muted border-border'
                                }`}
                                data-testid={`settings-pet-${pet.id}`}
                              >
                                <Icon className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                                <span className="text-xs font-medium">{pet.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Branding Colors */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Palette className="w-5 h-5" />
                          Branding Colors
                        </CardTitle>
                        <CardDescription>Choose your club's color scheme</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Presets */}
                        <div>
                          <Label className="mb-2 block text-sm">Color Presets</Label>
                          <div className="grid grid-cols-4 gap-2">
                            {COLOR_PRESETS.map(preset => (
                              <button
                                key={preset.name}
                                type="button"
                                onClick={() => selectColorPreset(preset)}
                                className={`p-2 rounded border text-center transition-all hover:border-primary/50 ${
                                  clubSettings.primary_color === preset.primary ? 'ring-2 ring-primary border-primary' : ''
                                }`}
                              >
                                <div className="flex gap-1 justify-center mb-1">
                                  <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.primary }} />
                                  <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.secondary }} />
                                  <div className="w-4 h-4 rounded border" style={{ backgroundColor: preset.accent }} />
                                </div>
                                <span className="text-xs">{preset.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Custom Colors */}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs">Primary</Label>
                            <div className="flex gap-2 items-center">
                              <input
                                type="color"
                                value={clubSettings.primary_color}
                                onChange={(e) => setClubSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                                className="w-8 h-8 rounded cursor-pointer"
                              />
                              <Input
                                value={clubSettings.primary_color}
                                onChange={(e) => setClubSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                                className="flex-1 text-xs h-8"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Secondary</Label>
                            <div className="flex gap-2 items-center">
                              <input
                                type="color"
                                value={clubSettings.secondary_color}
                                onChange={(e) => setClubSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                                className="w-8 h-8 rounded cursor-pointer"
                              />
                              <Input
                                value={clubSettings.secondary_color}
                                onChange={(e) => setClubSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                                className="flex-1 text-xs h-8"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Accent</Label>
                            <div className="flex gap-2 items-center">
                              <input
                                type="color"
                                value={clubSettings.accent_color}
                                onChange={(e) => setClubSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                                className="w-8 h-8 rounded cursor-pointer"
                              />
                              <Input
                                value={clubSettings.accent_color}
                                onChange={(e) => setClubSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                                className="flex-1 text-xs h-8"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Banner color — used for the club's top page banner background */}
                        <div>
                          <Label className="text-xs">Founding banner background</Label>
                          <p className="text-[11px] text-muted-foreground mb-1">
                            Background color for the founding-members banner (warm cream/amber tones work best for a welcoming feel).
                          </p>
                          <div className="flex gap-2 items-center max-w-xs">
                            <input
                              type="color"
                              value={clubSettings.banner_color || '#fffbeb'}
                              onChange={(e) => setClubSettings(prev => ({ ...prev, banner_color: e.target.value }))}
                              className="w-8 h-8 rounded cursor-pointer"
                              data-testid="club-banner-color-picker"
                            />
                            <Input
                              value={clubSettings.banner_color || '#fffbeb'}
                              onChange={(e) => setClubSettings(prev => ({ ...prev, banner_color: e.target.value }))}
                              className="flex-1 text-xs h-8"
                              data-testid="club-banner-color-input"
                            />
                          </div>
                        </div>

                        {/* Nav color — used for the top navigation bar background */}
                        <div>
                          <Label className="text-xs">Top nav background</Label>
                          <p className="text-[11px] text-muted-foreground mb-1">
                            Background color for the top navigation bar. Use a dark color (stone, forest, deep red) — text and icons render white on top.
                          </p>
                          <div className="flex gap-2 items-center max-w-xs">
                            <input
                              type="color"
                              value={clubSettings.nav_color || '#78350f'}
                              onChange={(e) => setClubSettings(prev => ({ ...prev, nav_color: e.target.value }))}
                              className="w-8 h-8 rounded cursor-pointer"
                              data-testid="club-nav-color-picker"
                            />
                            <Input
                              value={clubSettings.nav_color || '#78350f'}
                              onChange={(e) => setClubSettings(prev => ({ ...prev, nav_color: e.target.value }))}
                              className="flex-1 text-xs h-8"
                              data-testid="club-nav-color-input"
                            />
                          </div>
                        </div>

                        {/* Preview */}
                        <div>
                          <Label className="text-xs mb-2 block">Preview</Label>
                          <div 
                            className="rounded-lg p-4 text-white"
                            style={{ backgroundColor: clubSettings.primary_color }}
                          >
                            <div className="font-medium mb-2">{clubSettings.name || 'Club Name'}</div>
                            <div className="flex gap-2">
                              <span 
                                className="px-3 py-1 rounded text-xs"
                                style={{ backgroundColor: clubSettings.secondary_color }}
                              >
                                Button
                              </span>
                              <span 
                                className="px-3 py-1 rounded text-xs"
                                style={{ backgroundColor: clubSettings.accent_color, color: clubSettings.primary_color }}
                              >
                                Accent
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Button 
                      onClick={saveClubSettings} 
                      disabled={savingSettings}
                      className="w-full"
                      data-testid="save-settings-btn"
                    >
                      {savingSettings ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Save Settings</>
                      )}
                    </Button>
                  </TabsContent>

                  {/* Features Tab */}
                  <TabsContent value="overview" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Club Features</CardTitle>
                        <CardDescription>
                          Enable or disable features for your club
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {featureCatalog.map(feature => {
                          const Icon = FEATURE_ICONS[feature.id] || Settings;
                          const isEnabled = pendingFeatures[feature.id] ?? feature.default;
                          
                          return (
                            <div 
                              key={feature.id}
                              className="flex items-center justify-between p-3 border rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                  <Icon className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                  <div className="font-medium">{feature.name}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {feature.description}
                                  </div>
                                </div>
                              </div>
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={(checked) => {
                                  setPendingFeatures(prev => ({
                                    ...prev,
                                    [feature.id]: checked
                                  }));
                                }}
                                data-testid={`feature-toggle-${feature.id}`}
                              />
                            </div>
                          );
                        })}
                        
                        <Button 
                          onClick={saveFeatures} 
                          disabled={savingFeatures}
                          className="w-full"
                          data-testid="save-features-btn"
                        >
                          {savingFeatures ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            <><Save className="w-4 h-4 mr-2" /> Save Features</>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Members Tab */}
                  <TabsContent value="members" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Club Members</CardTitle>
                        <CardDescription>
                          Manage member roles and permissions
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Search */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            placeholder="Search members..."
                            value={memberSearch}
                            onChange={(e) => setMemberSearch(e.target.value)}
                            className="pl-10"
                            data-testid="member-search"
                          />
                        </div>

                        {/* Member List */}
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                          {filteredMembers.map(member => (
                            <div 
                              key={member.user_id}
                              className="flex items-center justify-between p-3 border rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                                  {member.avatar_url ? (
                                    <img 
                                      src={member.avatar_url} 
                                      alt="" 
                                      className="w-10 h-10 rounded-full object-cover"
                                    />
                                  ) : (
                                    <Users className="w-5 h-5 text-muted-foreground" />
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium flex items-center gap-2">
                                    {member.display_name || member.username}
                                    {member.role === 'creator' && (
                                      <Badge className="bg-amber-500 text-xs">Creator</Badge>
                                    )}
                                    {member.role === 'admin' && (
                                      <Badge variant="default" className="text-xs">Admin</Badge>
                                    )}
                                    {member.role === 'officer' && (
                                      <Badge variant="secondary" className="text-xs">Officer</Badge>
                                    )}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    @{member.username}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {member.can_promote && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => promoteToAdmin(member.user_id, member.username)}
                                    disabled={promotingUser === member.user_id}
                                    data-testid={`promote-${member.user_id}`}
                                  >
                                    {promotingUser === member.user_id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <UserPlus className="w-4 h-4 mr-1" />
                                        Make Admin
                                      </>
                                    )}
                                  </Button>
                                )}
                                {member.can_demote && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => demoteAdmin(member.user_id, member.username)}
                                    disabled={promotingUser === member.user_id}
                                    data-testid={`demote-${member.user_id}`}
                                  >
                                    {promotingUser === member.user_id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <UserMinus className="w-4 h-4 mr-1" />
                                        Remove Admin
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          {filteredMembers.length === 0 && (
                            <div className="text-center py-8 text-muted-foreground">
                              {memberSearch ? 'No members found' : 'No members yet'}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Ad Policy Tab */}
                  <TabsContent value="ads" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Ad Policy</CardTitle>
                        <CardDescription>
                          Control how ads are displayed to your members
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <div className="font-medium">Show Ads to Paid Members</div>
                            <div className="text-sm text-muted-foreground">
                              If enabled, paid members will still see ads. Otherwise, they get an ad-free experience.
                            </div>
                          </div>
                          <Switch
                            checked={pendingAdPolicy.show_ads_to_paid_members ?? false}
                            onCheckedChange={(checked) => {
                              setPendingAdPolicy(prev => ({
                                ...prev,
                                show_ads_to_paid_members: checked
                              }));
                            }}
                            data-testid="toggle-ads-paid-members"
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <div className="font-medium">Show Sponsor Ads</div>
                            <div className="text-sm text-muted-foreground">
                              Display ads from club sponsors and partners
                            </div>
                          </div>
                          <Switch
                            checked={pendingAdPolicy.show_sponsor_ads ?? true}
                            onCheckedChange={(checked) => {
                              setPendingAdPolicy(prev => ({
                                ...prev,
                                show_sponsor_ads: checked
                              }));
                            }}
                            data-testid="toggle-sponsor-ads"
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <div className="font-medium">Ad-Free for Premium</div>
                            <div className="text-sm text-muted-foreground">
                              Premium members always have an ad-free experience
                            </div>
                          </div>
                          <Switch
                            checked={pendingAdPolicy.ad_free_for_premium ?? true}
                            onCheckedChange={(checked) => {
                              setPendingAdPolicy(prev => ({
                                ...prev,
                                ad_free_for_premium: checked
                              }));
                            }}
                            data-testid="toggle-premium-adfree"
                          />
                        </div>

                        <Button 
                          onClick={saveAdPolicy} 
                          disabled={savingAdPolicy}
                          className="w-full"
                          data-testid="save-ad-policy-btn"
                        >
                          {savingAdPolicy ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            <><Save className="w-4 h-4 mr-2" /> Save Ad Policy</>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
